/* eslint-disable @typescript-eslint/no-use-before-define */
import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';
import {
  TIME_60_MINUTES_IN_MS,
  TIME_FIVE_MINUTES_IN_SECONDS,
  TIME_ONE_SECOND_IN_MS,
  TIME_ONE_SECOND_IN_SECONDS,
} from 'basics/constants/common.constants';
import { splitSecondsTimeInMinAndSec } from 'basics/utils/dates/dates.utils';
import useAuth from 'hooks/useAuth';

const useIdleTimerDialog = () => {
  const { t } = useTranslation();
  const { automaticLogout, automaticLogoutBcData } = useAuth();
  const [idleDialogIsOpen, setIdleDialogIsOpen] = useState(false);
  const [count, setCount] = useState(TIME_FIVE_MINUTES_IN_SECONDS);
  const [dialogContent, setDialogContent] = useState('');
  const intervalCountdown = useRef<NodeJS.Timer>(undefined);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const postBroadcastMessage = useCallback((data: string) => {
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage(data);
      } catch {
        // Channel might be closed, ignore
      }
    }
  }, []);

  useEffect(() => {
    broadcastChannelRef.current = new BroadcastChannel('idle');

    broadcastChannelRef.current.onmessage = (event: MessageEvent) => {
      if (event.data === automaticLogoutBcData) {
        automaticLogout();
      }
    };

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [automaticLogout, automaticLogoutBcData]);

  useEffect(() => {
    if (count > 0) {
      setDialogContent(`${t('idleTimerDialog_content', {
        time: splitSecondsTimeInMinAndSec({
          time: count,
          minutesLabel: t('short_minutes_label'),
          secondsLabel: t('short_seconds_label'),
        }),
      })}`);
    } else {
      automaticLogout();
      postBroadcastMessage(automaticLogoutBcData);
    }
  }, [count, automaticLogout, automaticLogoutBcData, postBroadcastMessage, t]);

  const onIdleTimerIdle = () => {
    setIdleDialogIsOpen(true);
    countDownTimerStart();
    intervalCountdown.current = setInterval(() => {
      setCount((previous) => previous - TIME_ONE_SECOND_IN_SECONDS);
    }, TIME_ONE_SECOND_IN_MS);
  };

  const { reset: idleTimerReset } = useIdleTimer({
    timeout: TIME_60_MINUTES_IN_MS,
    onIdle: onIdleTimerIdle,
    stopOnIdle: true,
    crossTab: true,
    name: 'react-idle-timer',
    syncTimers: 200,
  });

  const onCountDownActive = () => {
    setIdleDialogIsOpen(false);
    if (intervalCountdown.current) {
      clearInterval(intervalCountdown.current);
    }
    setCount(TIME_FIVE_MINUTES_IN_SECONDS);
    idleTimerReset();
  };

  const { start: countDownTimerStart } = useIdleTimer({
    timeout: 1, // instantly idle
    startOnMount: false,
    startManually: true,
    onActive: onCountDownActive,
    crossTab: true,
    name: 'countdown',
    syncTimers: 200,
    events: ['mousedown'],
  });

  return {
    count,
    dialogContent,
    idleDialogIsOpen,
    t,
  };
};

export default useIdleTimerDialog;
